import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

const STAGE_LABELS = {
  new_lead: "New lead", contacted: "Contacted", quoted: "Quoted",
  scheduled: "Scheduled", job_complete: "Job complete", repeat_recurring: "Repeat/Recurring",
};

export default function Pipeline() {
  const [board, setBoard] = useState(null);
  const [stages, setStages] = useState([]);
  const load = () => { api.get("/pipeline").then(r => { setBoard(r.data.board); setStages(r.data.stages); }); };
  useEffect(() => { load(); }, []);

  const move = async (contactId, currentStage, dir) => {
    const idx = stages.indexOf(currentStage);
    const next = stages[Math.max(0, Math.min(stages.length - 1, idx + dir))];
    if (next === currentStage) return;
    try {
      await api.patch(`/contacts/${contactId}`, { stage: next });
      toast.success(`Moved to ${STAGE_LABELS[next]}`);
      load();
    } catch { toast.error("Failed to move"); }
  };

  if (!board) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Pipeline</h1>
        <p className="text-slate-600 mt-1">Drag your customers from lead to loyal — one tap at a time.</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-6 -mx-4 px-4">
        {stages.map(stage => (
          <div key={stage} data-testid={T.pipeline.column(stage)} className="min-w-[290px] flex-shrink-0 rounded-xl bg-slate-100/60 border border-slate-200 p-3 flex flex-col owc-kanban-col max-h-[70vh]">
            <div className="flex items-center justify-between mb-3 sticky top-0">
              <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)] text-sm">{STAGE_LABELS[stage]}</div>
              <span className="text-xs font-bold text-slate-500">{(board[stage] || []).length}</span>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {(board[stage] || []).map(c => (
                <div key={c.id} data-testid={T.pipeline.card(c.id)} className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
                  <Link to={`/admin/contacts/${c.id}`} className="font-semibold text-[color:var(--owc-navy)] text-sm hover:text-[color:var(--owc-sky)]">{c.name}</Link>
                  <div className="text-xs text-slate-500 mt-1 truncate">{c.email || c.phone}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{c.address ? `${c.city || ""} ${c.zip || ""}` : "—"}</div>
                  <div className="mt-3 flex items-center gap-1">
                    <button data-testid={T.pipeline.movePrev(c.id)} disabled={stages.indexOf(stage) === 0} onClick={() => move(c.id, stage, -1)} className="p-1.5 rounded border border-slate-200 hover:border-[color:var(--owc-sky)] disabled:opacity-30"><ChevronLeft className="h-3 w-3" /></button>
                    <button data-testid={T.pipeline.moveNext(c.id)} disabled={stages.indexOf(stage) === stages.length - 1} onClick={() => move(c.id, stage, 1)} className="p-1.5 rounded border border-slate-200 hover:border-[color:var(--owc-sky)] disabled:opacity-30"><ChevronRight className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
              {(board[stage] || []).length === 0 && <div className="text-xs text-slate-400 py-6 text-center">Empty</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
